/**
 * #/mesa/:matchId — LA MESA DE CONTROL del acta electrónica.
 *
 * Es la pantalla del ANOTADOR en la laptop de la mesa, y es el hueco que
 * encontró la verificación de cierre de la Etapa 2: motor, rutas, seguridad e
 * imprimible estaban completos y probados, pero acreditar, resolver el
 * challenge y firmar/cerrar se hacía con curl. Tres pestañas, en el orden en
 * que pasan en la cancha:
 *
 *  1. **Oficiales** — cargar el cuerpo arbitral (`officials_set`) y emitir las
 *     credenciales de partido QR+PIN (ADR-019).
 *  2. **Challenge** — la interrupción en curso: ver el pedido, registrar el
 *     motivo, el desenlace del VCS y la reconciliación del marcador como UN
 *     lote atómico.
 *  3. **Acta** — la verificación de roster, el flujo de firmas de la
 *     instrucción 4.10 (quién firmó, quién falta, quién sigue) y el cierre con
 *     entrega.
 *
 * No es la consola del banco (`#/bench/:id/mesa`): aquélla atiende la cola de
 * intents con una tablet; ésta escribe el DOCUMENTO. Se dibuja dentro del
 * portal (con header) porque la opera alguien logueado con cuenta — el rol
 * `scout` es el e-anotador que la federación certifica.
 *
 * Los datos: `GET /sheet` (documento + challenge + designaciones) y
 * `GET /bench` (marcador y planteles, la misma proyección de las siete
 * pantallas). Se re-piden juntos tras cada acción con `reload` y, mientras el
 * acta está abierta, solos cada pocos segundos (más seguido en Challenge, que
 * es donde corre el reloj).
 *
 * ## EL PANEL
 *
 * Arriba, siempre a la vista: el marcador grande con los cupos de cada banco
 * y las DOS formaciones dibujadas sobre la cancha (la rotación vigente que
 * reduce el motor desde `lineup_set` y las sustituciones). Es lo que el
 * anotador controla contra el 2do árbitro antes de cada saque.
 */
import { useEffect, useState } from 'react';
import type { MatchRole, TeamSide } from '@scout/core';
import { useT } from '../../i18n/use-t';
import type { MessageKey } from '../../i18n/index';
import { Card, ErrorBox, Loading } from '../../components/States';
import { Court, playersFromRotation } from '../../components/Court';
import { errorMessage, useAsync } from '../../hooks';
import type { ApiClient, Identity, SheetResponse } from '../../lib/api-client';
import type { BenchSnapshot } from '../../lib/bench';
import { defaultKeyValueStore } from '../../lib/settings';
import { MESA_TABS, mesaHash, type MesaTab } from '../../lib/router';
import { ActaTab } from './ActaTab';
import { ChallengeTab } from './ChallengeTab';
import { OficialesTab } from './OficialesTab';

/** Clave i18n del rol de partido (los catorce están bajo `mesa.roles.*`). */
export function roleKey(role: MatchRole): MessageKey {
  return `mesa.roles.${role}` as MessageKey;
}

/**
 * `lastSeq` FRESCO, leído justo antes de numerar un evento de la mesa (mismo
 * criterio que el server al aceptar un intent: cuanto menos tiempo entre leer
 * y escribir, menos ventana para la carrera con otro puesto). Con `sinceSeq`
 * altísimo el API contesta el `lastSeq` del log entero sin mandar ni un
 * evento.
 */
export async function freshLastSeq(client: ApiClient, matchId: string): Promise<number> {
  const page = await client.getEvents(matchId, Number.MAX_SAFE_INTEGER);
  return page.lastSeq;
}

const TAB_KEYS: Record<MesaTab, MessageKey> = {
  oficiales: 'mesa.tabs.oficiales',
  challenge: 'mesa.tabs.challenge',
  acta: 'mesa.tabs.acta',
};

const PHASE_KEYS: Record<string, MessageKey> = {
  BEFORE_MATCH: 'mesa.phase.BEFORE_MATCH',
  AFTER_TOSS: 'mesa.phase.AFTER_TOSS',
  DURING_MATCH: 'mesa.phase.DURING_MATCH',
  AFTER_MATCH: 'mesa.phase.AFTER_MATCH',
  CLOSED: 'mesa.phase.CLOSED',
};

export interface MesaTabProps {
  client: ApiClient;
  matchId: string;
  sheet: SheetResponse;
  snapshot: BenchSnapshot | null;
  identity: Identity | null;
  /** Re-pide sheet y snapshot; se llama después de cada acción que escribe. */
  reload: () => void;
}

/** Refresco automático: el reloj del challenge necesita ver la ventana nueva enseguida. */
const POLL_CHALLENGE_MS = 3_000;
const POLL_DEFAULT_MS = 10_000;

const FLIP_KEY = 'scout.portal.mesa.flipped';

function loadFlip(): boolean {
  try {
    return defaultKeyValueStore().getItem(FLIP_KEY) === '1';
  } catch {
    return false;
  }
}

function saveFlip(value: boolean): void {
  try {
    defaultKeyValueStore().setItem(FLIP_KEY, value ? '1' : '0');
  } catch {
    /* modo privado: dura lo que la pestaña */
  }
}

export function MesaPage({
  client,
  matchId,
  tab,
  identity,
}: {
  client: ApiClient;
  matchId: string;
  tab: MesaTab;
  identity: Identity | null;
}): JSX.Element {
  const t = useT();
  const [flipped, setFlipped] = useState<boolean>(loadFlip);

  const sheet = useAsync(() => client.getSheet(matchId), [client, matchId]);
  // El snapshot es contexto (marcador, planteles): si esta credencial no lo
  // puede pedir, la mesa igual funciona con el documento solo.
  const snapshot = useAsync(
    () => client.getBenchSnapshot(matchId).catch(() => null),
    [client, matchId],
  );

  const reload = (): void => {
    sheet.reload();
    snapshot.reload();
  };

  const closed = sheet.data?.document.closed === true;
  const { reload: reloadSheet } = sheet;
  const { reload: reloadSnapshot } = snapshot;
  useEffect(() => {
    if (closed) return undefined;
    const every = tab === 'challenge' ? POLL_CHALLENGE_MS : POLL_DEFAULT_MS;
    const timer = setInterval(() => {
      reloadSheet();
      reloadSnapshot();
    }, every);
    return () => clearInterval(timer);
  }, [closed, tab, reloadSheet, reloadSnapshot]);

  // Sin dato y sin error = todavía cargando (incluye el primer render, antes
  // de que el efecto dispare el fetch): nunca un cartel rojo vacío.
  if (sheet.data === null && sheet.error === null) {
    return <Loading what={t('mesa.loadingWhat')} />;
  }
  if (sheet.data === null) {
    return <ErrorBox message={errorMessage(sheet.error)} onRetry={sheet.reload} />;
  }

  const doc = sheet.data;
  const snap = snapshot.data ?? null;
  const title = snap?.name && snap.name !== '' ? snap.name : matchId;
  const phaseKey = PHASE_KEYS[doc.document.phase];

  const tabProps: MesaTabProps = {
    client,
    matchId,
    sheet: doc,
    snapshot: snap,
    identity,
    reload,
  };

  return (
    <div className="mesa">
      <div className="page-head">
        <div>
          <h1>
            {t('mesa.title')} · {title}
          </h1>
          <div className="hint">
            {matchId}
            {snap?.competition ? ` · ${snap.competition}` : ''}
            {snap?.venue ? ` · ${snap.venue}` : ''}
          </div>
        </div>
        <div className="row">
          {phaseKey !== undefined ? (
            <span className={`chip ${doc.document.closed ? 'bad' : 'ok'}`}>{t(phaseKey)}</span>
          ) : null}
          {!doc.document.closed ? (
            <span className="chip muted" title={t('mesaPanel.autoRefreshTitle')}>
              <span className="dot" /> {t('mesaPanel.autoRefresh')}
            </span>
          ) : null}
          <button type="button" className="small" onClick={reload}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {doc.document.closed ? (
        <Card>
          <strong>{t('mesa.closedBanner')}</strong>
        </Card>
      ) : null}

      {snap !== null ? <MesaScore snapshot={snap} /> : null}

      {/* Panel: la cancha queda fija al costado mientras la mesa trabaja en la
          pestaña; en pantallas angostas se apila arriba. */}
      <div className={snap !== null ? 'mesa-layout' : undefined}>
        {snap !== null ? (
          <aside className="mesa-side">
            <MesaCourts
              snapshot={snap}
              flipped={flipped}
              onFlip={() => {
                setFlipped((prev) => {
                  saveFlip(!prev);
                  return !prev;
                });
              }}
            />
          </aside>
        ) : null}

        <div className="mesa-main">
          <nav className="tabs mesa-tabs">
            {MESA_TABS.map((name) => (
              <a key={name} href={mesaHash(matchId, name)} style={{ textDecoration: 'none' }}>
                <button type="button" className={`tab${tab === name ? ' active' : ''}`}>
                  {t(TAB_KEYS[name])}
                </button>
              </a>
            ))}
          </nav>

          {sheet.error !== null ? (
            <ErrorBox message={errorMessage(sheet.error)} onRetry={reload} />
          ) : null}

          {tab === 'oficiales' ? <OficialesTab {...tabProps} /> : null}
          {tab === 'challenge' ? <ChallengeTab {...tabProps} /> : null}
          {tab === 'acta' ? <ActaTab {...tabProps} /> : null}
        </div>
      </div>
    </div>
  );
}

const SIDES: readonly TeamSide[] = ['home', 'away'];

/** El marcador del panel: puntos grandes, sets al medio, cupos de cada banco. */
export function MesaScore({ snapshot }: { snapshot: BenchSnapshot }): JSX.Element {
  const t = useT();
  const [home, away] = SIDES.map((side) => (
    <div key={side} className={`mesa-team ${side}`}>
      <span className="mesa-team-name">{snapshot.meta[side].name}</span>
      {/* El lugar del cartel de saque se reserva siempre: si no, los puntos de
          los dos equipos quedan a distinta altura según quién saca. */}
      <span
        className="serve-tag"
        title={snapshot.servingTeam === side ? t('bench.score.serving') : undefined}
        style={snapshot.servingTeam === side ? undefined : { visibility: 'hidden' }}
        aria-hidden={snapshot.servingTeam === side ? undefined : true}
      >
        <span className="dot" /> {t('bench.score.servingShort')}
      </span>
      <span className="mesa-points">{snapshot.teams[side].score}</span>
      <span className="mesa-quota">
        <span>
          <b>{snapshot.teams[side].timeoutsLimit - snapshot.teams[side].timeoutsUsed}</b>{' '}
          {t('bench.score.timeoutsLeft')}
        </span>
        <span>
          <b>{snapshot.teams[side].substitutions.remaining}</b> {t('bench.score.subsLeft')}
        </span>
        <span>
          <b>{snapshot.teams[side].challengesRemaining}</b> {t('bench.score.challengesLeft')}
        </span>
      </span>
    </div>
  ));
  return (
    <section className="mesa-score" aria-label={t('mesaPanel.scoreLabel')}>
      <div className="mesa-score-body">
        {home}
        <div className="mesa-center">
          <span className="label">{t('common.set', { set: snapshot.currentSet })}</span>
          <span className="sets">
            {snapshot.setsWon.home}–{snapshot.setsWon.away}
          </span>
          <span className="label">{t('common.sets')}</span>
          {snapshot.finishedSets.length > 0 ? (
            <span className="pills">
              {snapshot.finishedSets.map((s) => (
                <span key={s.setNumber}>
                  {s.home}–{s.away}
                </span>
              ))}
            </span>
          ) : null}
        </div>
        {away}
      </div>
    </section>
  );
}

/** Las dos formaciones en cancha completa (local abajo salvo que se gire). */
export function MesaCourts({
  snapshot,
  flipped,
  onFlip,
}: {
  snapshot: BenchSnapshot;
  flipped: boolean;
  onFlip: () => void;
}): JSX.Element {
  const t = useT();
  const sinFormacion = SIDES.every((side) => snapshot.teams[side].rotation.length === 0);
  const side = (team: TeamSide) => ({
    team,
    name: snapshot.meta[team].name,
    players: playersFromRotation(
      snapshot.teams[team].rotation,
      snapshot.meta[team].players,
      snapshot.meta[team].liberos,
    ),
    serving: snapshot.servingTeam === team,
    zonePrefix: 'P',
  });
  return (
    <section className="mesa-courts">
      <div className="mesa-courts-head">
        <h2>{t('mesaPanel.lineupsTitle', { set: snapshot.currentSet })}</h2>
        <span className="spacer" />
        <button type="button" className="small" onClick={onFlip} aria-pressed={flipped}>
          {flipped ? t('bench.court.unflip') : t('bench.court.flip')}
        </button>
      </div>
      <Court
        label={t('mesaPanel.courtLabel', {
          home: snapshot.meta.home.name,
          away: snapshot.meta.away.name,
        })}
        sides={[side('home'), side('away')]}
        rotated={flipped}
        width={268}
      />
      {sinFormacion ? <p className="hint">{t('bench.court.noLineup')}</p> : null}
    </section>
  );
}
